import type React from 'react';

// Shared URL linkification for every surface that renders user-typed text as
// PLAIN TEXT in the browser (ticket 79).
//
// Regular timeline statuses never come through here: they arrive from the
// server as pre-linkified HTML. The surfaces that do are the ones the server
// cannot linkify for us — DM and group-chat bodies (the server only ever holds
// ciphertext and never sees the words) and room chat (rendered from plain text
// client-side). Those two shipped separate renderers, and the room one was
// missed when DMs were fixed.
//
// This module exists so there is exactly ONE parser and ONE set of anchor
// attributes behind all of them. Two copies of a link parser is two places for
// the security properties below to drift apart, and the one that drifts is
// always the one nobody was looking at.

// Scheme handling is an ALLOWLIST, not a blocklist. Message text is
// attacker-controlled by definition — anyone can DM you, and anyone in a public
// room can put a line in front of everybody at once. PLATFORM_SECURITY_REVIEW.md
// already carries FED-LOW-2 for `javascript:` links surviving elsewhere in this
// codebase. Matching only http(s) means a `javascript:` or `data:` payload stays
// inert text rather than becoming a clickable script. The leading \b stops
// `xhttps://…` matching mid-word.
const URL_RE = /\bhttps?:\/\/[^\s<>"'`]+/gi;
const ALLOWED_SCHEME_RE = /^https?:\/\/\S/i;

// Trailing sentence punctuation belongs to the sentence, not the URL:
// "look at https://example.com." must not link a trailing full stop.
const TRAILING_PUNCTUATION = '.,;:!?\'"*_~';
const CLOSERS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

const countOf = (text: string, ch: string): number => {
  let n = 0;
  for (const c of text) if (c === ch) n += 1;
  return n;
};

// A URL may legitimately END in a bracket (wikipedia disambiguation links), so
// only strip a closer that has no opener to match it inside the URL.
export const trimUrlTail = (raw: string): string => {
  let url = raw;
  for (;;) {
    const last = url.charAt(url.length - 1);
    if (!last) break;
    if (TRAILING_PUNCTUATION.includes(last)) {
      url = url.slice(0, -1);
      continue;
    }
    const opener = CLOSERS[last];
    if (opener && countOf(url, opener) < countOf(url, last)) {
      url = url.slice(0, -1);
      continue;
    }
    break;
  }
  return url;
};

// What the reader SEES. The href always carries the whole URL; only the label
// is shortened, and it is always a literal PREFIX of the real destination —
// never a re-written or prettified version of it. A chat line is a phishing
// surface, so "what you see is the start of where you are going" is the
// property worth keeping. A pasted tracking URL runs to hundreds of characters
// and would otherwise shred the layout.
const MAX_LINK_TEXT = 48;
export const linkLabel = (url: string): string =>
  url.length <= MAX_LINK_TEXT ? url : `${url.slice(0, MAX_LINK_TEXT)}…`;

// Graceful degradation: message text is capped server-side, which still leaves
// room for hundreds of URLs. Past this many, the rest stay as plain text rather
// than emitting an unbounded pile of anchors.
export const MAX_LINKS = 50;

export interface LinkifyTextToken {
  type: 'text';
  value: string;
  /** Offset in the original string — callers use it for stable React keys. */
  start: number;
}

export interface LinkifyUrlToken {
  type: 'url';
  value: string;
  start: number;
  /**
   * Past MAX_LINKS. Render the value verbatim and run NO further passes over
   * it: a URL that is not a link is still not prose, and letting the mention
   * pass at it would tear the `@bob` out of `https://example.com/@bob`.
   */
  overflow: boolean;
}

export type LinkifyToken = LinkifyTextToken | LinkifyUrlToken;

/**
 * Split plain text into URL and non-URL runs.
 *
 * URLs are found FIRST, and every other pass (mentions, emoji, emotes) must run
 * only over the `text` tokens. That ordering is not a preference: with mentions
 * matched first, the `@bob` in https://example.com/@bob was pulled out of the
 * middle of the URL and linked to a local profile. That was a live bug in the
 * DM renderer and would have been a live bug in room chat too.
 */
export const tokenizeLinks = (text: string): LinkifyToken[] => {
  const out: LinkifyToken[] = [];
  let lastIdx = 0;
  let linkCount = 0;
  let match: RegExpExecArray | null;
  // Reset regex state because of the /g flag (it's stateful).
  URL_RE.lastIndex = 0;

  while ((match = URL_RE.exec(text)) !== null) {
    const start = match.index;
    const url = trimUrlTail(match[0]);

    // Belt and braces over the regex: a URL token is only ever emitted for a
    // scheme on the allowlist, so a future loosening of URL_RE cannot quietly
    // turn arbitrary text into a clickable href.
    if (!url || !ALLOWED_SCHEME_RE.test(url)) {
      URL_RE.lastIndex = start + match[0].length;
      continue;
    }

    if (start > lastIdx) {
      out.push({
        type: 'text',
        value: text.slice(lastIdx, start),
        start: lastIdx,
      });
    }

    out.push({
      type: 'url',
      value: url,
      start,
      overflow: linkCount >= MAX_LINKS,
    });
    linkCount += 1;

    lastIdx = start + url.length;
    // Resume after the TRIMMED url, so punctuation we declined to swallow is
    // still tokenized as text rather than skipped.
    URL_RE.lastIndex = lastIdx;
  }

  if (lastIdx < text.length) {
    out.push({ type: 'text', value: text.slice(lastIdx), start: lastIdx });
  }
  return out;
};

/**
 * The one anchor every linkified surface emits. The attributes live here rather
 * than at each call site so no surface can end up with a weaker `rel` than
 * another.
 *
 * A plain function rather than a component on purpose: it emits the `<a>`
 * itself, so a caller's output is the same element tree it would have built by
 * hand. Wrapping it in a component would hide the anchor one level down and
 * quietly change what every test of those callers is looking at.
 *
 * `noreferrer` is a PRIVACY property here, not boilerplate: without it the
 * destination site is handed the URL of the private thread or room the reader
 * was sitting in. `noopener` keeps the opened tab from reaching back into this
 * one.
 */
// Hoisted rather than inline: a link inside a chat line or a message bubble
// sits on a row that is itself clickable (jump-to-message, open thread), and
// opening the link must not also fire that.
const stopPropagation = (e: React.MouseEvent) => {
  e.stopPropagation();
};

export const externalLinkWithLabel = (
  url: string,
  label: React.ReactNode,
  className: string,
  key: string,
): React.ReactElement => (
  <a
    key={key}
    href={url}
    className={className}
    target='_blank'
    rel='noopener noreferrer nofollow'
    title={url}
    onClick={stopPropagation}
  >
    {label}
  </a>
);

/**
 * A link whose visible text IS the URL. The overwhelmingly common case: a bare
 * URL found in plain text.
 *
 * Markdown-style `[label](url)` links (the kanban board, ticket 86) carry an
 * author-chosen label instead and go through `externalLinkWithLabel` — but they
 * come out of the SAME function underneath, so a surface with a hand-written
 * label can never end up with a weaker `rel` than one without.
 */
export const externalLink = (
  url: string,
  className: string,
  key: string,
): React.ReactElement =>
  externalLinkWithLabel(url, linkLabel(url), className, key);
